import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OnboardingProvider } from "@/context/OnboardingContext";
import { ProductTour, HelpCenter, HelpButton, WelcomeModal } from "@/components/onboarding";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import "@/App.css";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import ProviderDashboard from "@/pages/ProviderDashboard";
import PostJobPage from "@/pages/PostJobPage";
import JobDetailPage from "@/pages/JobDetailPage";
import JobBrowsePage from "@/pages/JobBrowsePage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import VerificationPage from "@/pages/VerificationPage";
import AdminPage from "@/pages/AdminPage";
import NotificationSettingsPage from "@/pages/NotificationSettingsPage";
import StripeConnectPage from "@/pages/StripeConnectPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    // Pass the current location so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === 'customer' ? '/dashboard' : '/provider'} replace />;
  }
  
  return children;
};

// Dashboard Router
const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  return user.role === 'customer' 
    ? <Navigate to="/dashboard" replace /> 
    : <Navigate to="/provider" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* Payment Routes */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      
      {/* Subscription Route */}
      <Route 
        path="/subscribe" 
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <SubscriptionPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Verification Route */}
      <Route 
        path="/verification" 
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <VerificationPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Job Browse Route for Providers */}
      <Route 
        path="/jobs/browse" 
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <JobBrowsePage />
          </ProtectedRoute>
        } 
      />
      
      {/* Stripe Connect Routes */}
      <Route 
        path="/provider/payouts" 
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <StripeConnectPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/provider/stripe-return" 
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <Navigate to="/provider/payouts?status=success" replace />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/provider/stripe-refresh" 
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <Navigate to="/provider/payouts?status=refresh" replace />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Route */}
      <Route path="/admin" element={<AdminPage />} />
      
      {/* Settings Routes */}
      <Route 
        path="/settings/notifications" 
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Auto-redirect to correct dashboard */}
      <Route path="/app" element={<DashboardRouter />} />
      
      {/* Customer Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/post-job" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <PostJobPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/job/:jobId" 
        element={
          <ProtectedRoute>
            <JobDetailPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Provider Routes */}
      <Route 
        path="/provider" 
        element={
          <ProtectedRoute allowedRoles={['provider']}>
            <ProviderDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Onboarding Components Wrapper
const OnboardingComponents = () => {
  const { user } = useAuth();
  
  // Only show onboarding for logged-in users
  if (!user) return null;
  
  return (
    <>
      <ProductTour />
      <HelpCenter userRole={user.role} />
      <HelpButton />
      <WelcomeModal userRole={user.role} userName={user.full_name} userId={user.id} />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OnboardingProvider>
          <AppRoutes />
          <OnboardingComponents />
          <PWAInstallPrompt />
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'Manrope, sans-serif',
              },
            }}
          />
        </OnboardingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
